import React from 'react'
import { useSelector, Provider } from 'react-redux'
import { Snackbar } from '@material-ui/core'
import { useTheme, makeStyles } from '@material-ui/core/styles'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { ErrorOutline, CheckCircleOutlineOutlined, ReportProblemOutlined, Close } from '@material-ui/icons'

import { createReduxDataAndStore } from '../virtusa-redux'
import { ScreenCover } from '../styles'
import { toastZIndex } from '../common'

const reducerKey = 'toast'
const defaultAutoHideDuration = 6000
const toastMessagePadding = '10px 10px 10px 0px'
const titlePadding = '10px'
const toastWidth = 'fit-content'
const iconContainerWidth = '70px'
const toastBorderRadius = '4px'

// Define the redux initial state at the reducer key
const initialState = {
  id: reducerKey,
  toastOn: false,
  isBlocking: false,
  component: undefined,
  autoHideDuration: null,
  title: '',
  message: '',
  backgroundIcon: '',
  top: undefined,
  right: undefined
}

const { setState, getState, store } = createReduxDataAndStore(reducerKey, initialState)

const toastSelector = () =>
  getState().toastOn

/**
 * Set the values to turn on the toast and display a message. See src/common/constants.js for
 * various styles for the toast that are permitted.
 *
 * @param {object} toastObj - The toast object described in the initialState above.
 * @returns {void} Nothing
 */
export const setToastOn = toastObj => {
  setState({ ...toastObj, toastOn: true })
}

/**
 * Turn off the toast.
 *
 * @returns {void} Nothing
 */
export const setToastOff = () => {
  setState(initialState)
}

const ToastBase = ({ toast }) => {
  const handleClose = (_event, reason) => {
    if (reason === 'clickaway') {
      return
    }

    setToastOff()
  }
  const useStyles = makeStyles({
    anchorOriginTopCenter: {
      top: toast.top
    }
  })

  const classes = useStyles()
  return (
    <Snackbar
      id={toast.id} 
      classes={classes} 
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      open={toast.toastOn} 
      autoHideDuration={toast.autoHideDuration} 
      onClose={handleClose}
    >
      <toast.component onClose={handleClose} toast={toast} />
    </Snackbar>
  )
}

ToastBase.propTypes = {
  toast: PropTypes.object,
}

const BlockingToast = ({ toast }) =>
  <ScreenCover zIndex={toastZIndex}>
    <ToastBase toast={toast} />
  </ScreenCover>

BlockingToast.propTypes = {
  toast: PropTypes.object,
}

const ToastWithStore = () => {
  const toastOn = useSelector(toastSelector)
  if (!toastOn) {
    return null
  }
  const toast = getState()
  if (toast.isBlocking) {
    return <BlockingToast toast={toast} />
  }
  return (
    <ToastBase toast={toast} />
  )
}

export const Toast = () => {
  return (
    <Provider store={store}>
      <ToastWithStore />
    </Provider>
  )
}

const BaseToastBodyContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: ${toastWidth};
  box-shadow: rgba(0, 0, 0, 0.15) 1.95px 1.95px 2.6px;
  background-color: ${props => props.backgroundColor};
  color: ${props => props.color};
  border-radius: ${toastBorderRadius};
  cursor: pointer;
`

const ToastIconContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: flex-start;
  padding-top: 9px;
  background-color: ${props => props.backgroundIcon};
  width: ${iconContainerWidth};
`

const ToastTitleMessageContainer = styled.div`
  width: 100%;
  padding: ${toastMessagePadding};
`

const ToastTitleContainer = styled.div`
  font-weight: ${props => props.theme.typography.fontWeightBold};
  font-size: ${props => props.theme.typography.toast.fontSizeTitle};
  padding-bottom: ${titlePadding};
`

const ToastMessageContainer = styled.div`
  font-size: ${props => props.theme.typography.toast.fontSizeMessage};
`
const CloseIconContainer = styled.div`
  margin-right: 10px;
  padding: ${toastMessagePadding};
`

export const BaseToastBody = props => {
  const theme = useTheme()
  const { toast } = props
  const color = theme.palette.toast.color
  let backgroundColor
  let icon
  switch (toast.severity) {
    case 'success' :
      backgroundColor = theme.palette.toast.backgroundSuccess
      icon = <CheckCircleOutlineOutlined />
      break
    case 'warning' :
      backgroundColor = theme.palette.toast.backgroundWarning
      icon = <ReportProblemOutlined />
      break
    case 'info' :
      backgroundColor = theme.palette.toast.backgroundInfo
      icon = <ErrorOutline />
      break
    case 'error' :
      backgroundColor = theme.palette.toast.backgroundError
      icon = <ErrorOutline />
      break
  }
  const onClick = () => {
    props.onClose()
  }
  return (
    <BaseToastBodyContainer onClick={onClick} color={color} backgroundColor={backgroundColor}>
      <ToastIconContainer>
        {icon}
      </ToastIconContainer>
      <ToastTitleMessageContainer theme={theme}>
        {toast.title
          ? <ToastTitleContainer theme={theme}>
            {toast.title}
          </ToastTitleContainer>
          : null
        }
        <ToastMessageContainer theme={theme}>
          {toast.message}
        </ToastMessageContainer>
      </ToastTitleMessageContainer>
      {toast.closeIcon 
        ? <CloseIconContainer>
          <Close fontSize="small" scale={0.7} />
        </CloseIconContainer>
        : null
      }
    </BaseToastBodyContainer>
  )
} 

BaseToastBody.propTypes = {
  toast: PropTypes.object,
  onClose: PropTypes.func
}

export const baseToast = ({ message, title, autoHideDuration, isBlocking, top, severity, closeIcon }) => {
  setToastOn({ 
    message, 
    title,
    top,
    severity,
    component: BaseToastBody, 
    autoHideDuration: autoHideDuration,
    isBlocking: isBlocking === undefined ? true : isBlocking,
    closeIcon: closeIcon === undefined ? false : closeIcon
  })
}

export const successToast = ({ message, title, autoHideDuration, isBlocking, top, closeIcon }) => {
  baseToast({ 
    message,
    title,
    top,
    severity: 'success',
    autoHideDuration: autoHideDuration === undefined ? defaultAutoHideDuration : autoHideDuration,
    isBlocking: isBlocking === undefined ? false : isBlocking,
    closeIcon: closeIcon
  })
}

export const errorToast = ({ message, title, autoHideDuration, isBlocking, top, closeIcon }) => {
  baseToast({ 
    message, 
    title,
    top,
    severity: 'error',
    autoHideDuration: autoHideDuration,
    isBlocking: isBlocking,
    closeIcon: closeIcon
  })
}

export const warningToast = ({ message, title, autoHideDuration, isBlocking, top, closeIcon }) => {
  baseToast({ 
    message,
    title,
    top,
    severity: 'warning',
    autoHideDuration: autoHideDuration,
    isBlocking: isBlocking,
    closeIcon: closeIcon
  })
}

export const infoToast = ({ message, title, autoHideDuration, isBlocking, top, closeIcon }) => {
  baseToast({ 
    message,
    title,
    top,
    severity: 'info',
    autoHideDuration: autoHideDuration,
    isBlocking: isBlocking,
    closeIcon: closeIcon
  })
}